import {
  Checkbox as NextUICheckbox,
  CheckboxProps as NextUICheckboxProps,
} from "@heroui/react";
import clsx from "clsx";

type Color = Extract<NextUICheckboxProps["color"], "primary">;
type Size = Extract<NextUICheckboxProps["size"], "sm" | "md" | "lg">;

export type CheckboxProps = Omit<
  NextUICheckboxProps,
  "color" | "onClick" | "children"
> & {
  color?: Color;
  label: string;
  onClick?: never;
  size?: Size;
  ref?: React.Ref<HTMLInputElement>;
};

export function Checkbox({
  color = "primary",
  label,
  ref,
  size = "md",
  ...props
}: CheckboxProps) {
  return (
    <NextUICheckbox
      {...props}
      ref={ref}
      classNames={{
        base: clsx({
          "opacity-100": !props.isDisabled,
          "pointer-events-none opacity-50": props.isDisabled,
        }),
        icon: clsx("stroke-lg", {
          "h-3.5 w-3": size === "sm",
          "h-3.75 w-4": size === "md",
          "h-4 w-5": size === "lg",
        }),
        label: clsx("text-default-900", {
          "after:ms-0.5 after:content-['*']": props.isRequired,
          "inline-flex items-center justify-center group-data-[selected=true]:opacity-60 before:absolute before:h-0.5 before:w-0 before:bg-foreground before:content-[''] group-data-[selected=true]:before:w-full":
            props.lineThrough,
          "text-danger": props.isInvalid,
          "text-large": size === "lg",
          "text-small": size === "sm" || size === "md",
        }),
        wrapper: clsx("", {
          "before:border-danger": props.isInvalid,
          "h-4 w-4 rounded-[4px] before:rounded-[4px] after:rounded-[4px]":
            size === "sm",
          "h-5 w-5 rounded-[5px] before:rounded-[5px] after:rounded-[5px]":
            size === "md",
          "h-6 w-6 rounded-[6px] before:rounded-[6px] after:rounded-[6px]":
            size === "lg",
          "text-primary-foreground group-data-[hover=true]:before:border-primary group-data-[hover=true]:before:bg-primary-light after:bg-primary after:text-primary-foreground":
            color === "primary",
        }),
      }}
      data-testid={`${props.name}${props.value ? `-${props.value}` : ""}`}
    >
      {label}
    </NextUICheckbox>
  );
}

Checkbox.displayName = "RecareUI.Checkbox";
